import React from 'react';
import { motion } from 'framer-motion';
import Award from 'lucide-react/dist/esm/icons/award';
import Users from 'lucide-react/dist/esm/icons/users';
import MapPin from 'lucide-react/dist/esm/icons/map-pin';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import Target from 'lucide-react/dist/esm/icons/target';
import Heart from 'lucide-react/dist/esm/icons/heart';
import Lightbulb from 'lucide-react/dist/esm/icons/lightbulb';
import Handshake from 'lucide-react/dist/esm/icons/handshake';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest professional standards in all our engagements',
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'Transparency and ethical conduct guide every decision we make',
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We continuously evolve our services to meet changing business needs',
    },
    {
      icon: Handshake,
      title: 'Partnership',
      description: 'We build lasting relationships founded on mutual trust and respect',
    },
    {
      icon: TrendingUp,
      title: 'Empowerment',
      description: "We contribute to South Africa's economic transformation through skills development",
    },
  ];

  const qualifications = [
    'Bachelor of Accounting Science (UNISA)',
    'Honours Bachelor of Accounting (UNISA)',
    'Post Graduate Certificate in Auditing (ATP)',
    'Chartered Accountant (South Africa)',
    'Registered Auditor (South Africa)',
  ];

  const experience = [
    'Partner - OMC Chartered Accountants (SA)',
    'Director - Camelsa Chartered Accountants (SA)',
    'Chief Financial Officer - SAEDF',
    'Senior Manager - PricewaterhouseCoopers',
    'Manager - KPMG',
  ];

  const accreditations = [
    {
      title: 'SAICA Membership',
      description: 'Ensuring adherence to the highest professional standards',
    },
    {
      title: 'LG SETA Accreditation',
      description: 'Recognised for our training and development capabilities',
    },
    {
      title: 'GRAP Training Certification',
      description: 'Qualified trainers in Government Reporting Standards',
    },
    {
      title: 'Auditor General Partnership',
      description: 'Trusted partners in public sector audit delivery',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-secondary py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div className="text-white" {...fadeInUp}>
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold leading-snug md:leading-normal lg:leading-[1.25] mb-6"><span className="text-[#00A9F4]">Trusted Advisors</span> Across Public and Private Sectors</h1>
              <p className="text-xl md:text-xl text-blue-100 max-w-4xl mx-auto">
                A black-owned chartered accounting firm advancing South Africa’s financial governance through trust, innovation, and over two decades of proven expertise.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src="https://media.phst.co.za/file/omc/omc-about-1.jpg?w=600&h=400&fit=crop"
                alt="Professional accounting office"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Who We Are</h2>
              <div className="space-y-6 text-lg text-gray-600">
                <p>
                  OMC Chartered Accountants is a proudly black-owned and black-managed chartered accounting firm that has been at the forefront of financial excellence in South Africa for over two decades. We specialise in public sector finance, drawing from our extensive experience working within government entities, municipalities, and regulatory bodies.
                </p>
                <p>
                  Our firm represents the perfect blend of local insight and international best practices. Founded with a singular vision of providing comprehensive professional services to South African entities, we have grown to become a trusted partner for businesses, government departments, and organisations across multiple provinces.
                </p>
                <p>
                  What sets us apart is our leadership team's unique background. Having served in senior positions including Municipal Managers, Chief Financial Officers, Treasury Directors, and various Financial Management roles, we bring insider knowledge that translates into practical, effective solutions for our clients.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="bg-background p-8 rounded-2xl"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Story</h3>
              <div className="space-y-4 text-gray-600">
                <div className="flex items-start">
                  <div className="w-4 h-4 bg-primary rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900">2001</h4>
                    <p>Established as Camelsa Chartered Accountants</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-4 h-4 bg-secondary rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900">2008</h4>
                    <p>Rebranded to OMC Chartered Accountants</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-4 h-4 bg-accent rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Present</h4>
                    <p>Leading chartered accounting firm across multiple provinces</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Leadership</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the visionary leader behind OMC Chartered Accountants
            </p>
          </motion.div>
          <div className="max-w-4xl mx-auto">
            <motion.div className="bg-white rounded-2xl shadow-lg p-8 md:p-12" {...fadeInUp}>
              <div className="text-center mb-8">
                                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Oliver Chikweshe CA (SA), RA (SA)
                </h3>
                <p className="text-xl text-primary font-semibold">Managing Partner & Founder</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Qualifications</h4>
                  <ul className="space-y-2">
                    {qualifications.map((qual, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{qual}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Career Highlights</h4>
                  <ul className="space-y-2">
                    {experience.map((exp, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-secondary mt-0.5 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{exp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="bg-background p-6 rounded-xl">
                <p className="text-gray-600 leading-relaxed">
                  Oliver's extensive experience encompasses assurance audits for both provincial government departments and local authorities, business advisory services, forensic investigations, and strategic financial management. His unique perspective, gained from serving as a Chief Financial Officer of a municipality and chairing District CFO Forums, enables OMC to deliver unparalleled insight into municipal financial management and regulatory requirements.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {values.map((value, index) => (
              <motion.div key={index} className="text-center p-6 rounded-xl shadow-lg" variants={fadeInUp}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary text-white rounded-full mb-6">
                  <value.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Presence Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Presence</h2>
              <p className="text-lg text-gray-600 mb-8">
                With fully-fledged or representative offices across South Africa, we bring our expertise directly to where our clients need us most.
              </p>
              <div className="grid grid-cols-2 gap-4 text-gray-600">
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-primary mr-2" />
                  <span>Gauteng</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-primary mr-2" />
                  <span>Limpopo</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-primary mr-2" />
                  <span>Mpumalanga</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-primary mr-2" />
                  <span>Free State</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-primary mr-2" />
                  <span>KwaZulu-Natal</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-primary mr-2" />
                  <span>Eastern Cape</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-primary mr-2" />
                  <span>Western Cape</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="bg-white p-8 rounded-2xl shadow-lg"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Professional Credentials</h3>
              <div className="space-y-6">
                {accreditations.map((accred, index) => (
                  <div key={index} className="flex items-start">
                    <Award className="h-6 w-6 text-primary mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{accred.title}</h4>
                      <p className="text-gray-600">{accred.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;